import { Anchor } from './MarketingShell';
import { Band, Card, Boundaries, FounderPortrait } from './sections';

/**
 * Who is behind the product.
 *
 * A buyer in primary care is not really asking "who founded this company"; they
 * are asking "will the person on the other end of this understand what a
 * service specification does to a Tuesday". So the page leads with the
 * operational background and the deliberate choices, and does not manufacture
 * a team page, an investor list, or a founding date presented as heritage.
 */
export function AboutPage() {
  return (
    <>
      <Band
        title="Built by people who have delivered NHS services, not just built software"
        heading="h1"
        lede="InBrief is an independent software product for UK primary care organisations. It is
              not affiliated with, endorsed by or accredited by the NHS."
      >
        <div className="mk-founder mk-founder-with-portrait">
          <FounderPortrait />
          <div className="mk-founder-text">
            <p>
              Louis Williams founded InBrief after several years working in NHS primary care
              operations and transformation. Having managed contracts, service specifications and
              performance across GP practices and PCNs, he recognised that too much operational
              knowledge remained locked away in lengthy documents or depended on a handful of
              experienced individuals.
            </p>
            <p>
              InBrief was built to solve that problem, giving primary care teams instant access to
              trusted, source-backed operational guidance, while helping practices turn complex
              requirements into clear actions and patient communications.
            </p>
          </div>
        </div>
      </Band>

      <Band title="What we decided not to build" tone="tint"
        lede="The constraints below cost us features. They are the reason the product can be
              trusted with a contract, and they are not up for negotiation in a sales call.">
        <div className="mk-three">
          <Card title="No patient data">
            <p>
              Nothing patient-identifiable enters the system, and uploads that look like they
              contain it are refused. That rules out a whole category of features, and removes a
              whole category of risk.
            </p>
          </Card>
          <Card title="No clinical advice">
            <p>
              InBrief reads administrative documents and drafts administrative communications. It
              does not diagnose, triage, or recommend treatment — which is what keeps it outside
              the medical device regime.
            </p>
          </Card>
          <Card title="No unsourced answers">
            <p>
              An item that cannot be traced back to a passage is discarded rather than displayed.
              It would have been easier to show more and cite less; it would also have been
              useless the first time someone checked.
            </p>
          </Card>
        </div>
      </Band>

      <Band title="Where we are">
        <p className="mk-prose">
          InBrief is early. It is being used against real contracts, and the paid pilot exists
          precisely so that an organisation can test it on a specification it actually holds
          before committing to anything. We would rather say that plainly than describe a customer
          base we do not yet have.
        </p>
        <p className="mk-prose">
          If you would like to shape what gets built next, the founding customer cohort is a fixed
          group with a subscription discount, assisted onboarding, and a direct line to the person
          writing the roadmap.
        </p>
        <div className="mk-hero-actions">
          <Anchor to="/contact" className="btn btn-primary">Talk to us</Anchor>
          <Anchor to="/pricing" className="btn btn-secondary">Pilots and pricing</Anchor>
        </div>
      </Band>

      <Band title="Where the product stops" tone="tint">
        <Boundaries />
        <p className="mk-small mk-boundary-note">
          InBrief is not affiliated with or endorsed by the NHS.
        </p>
      </Band>
    </>
  );
}
